"use client";

import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { MapIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { unstable_serialize } from "swr/infinite";

import { ChatHeader } from "@/components/chat-header";
import { FloodMap } from "@/components/flood-map";
import { useArtifactSelector } from "@/hooks/use-artifact";
import { useAutoResume } from "@/hooks/use-auto-resume";
import { useChatVisibility } from "@/hooks/use-chat-visibility";
import { useMap } from "@/hooks/use-map";
import type { Vote } from "@/lib/db/schema";
import type { Attachment, ChatMessage, MapLayer } from "@/lib/types";
import type { AppUsage } from "@/lib/usage";
import { 
  fetcher, 
  fetchWithErrorHandlers, 
  generateUUID, 
  isRenderableMessage
} from "@/lib/utils";
import { Artifact } from "./artifact";
import { handleOnData } from "./stream-handler";
import { Messages } from "./messages";
import { MultimodalInput } from "./multimodal-input";
import { getChatHistoryPaginationKey } from "./sidebar-history";
import type { VisibilityType } from "./visibility-selector";

export function Chat({
  id,
  initialMessages,
  initialChatModel,
  initialVisibilityType,
  isReadonly,
  autoResume,
  initialLastContext,
  initialMapArtifacts = [],
}: {
  id: string;
  initialMessages: ChatMessage[];
  initialChatModel: string;
  initialVisibilityType: VisibilityType;
  isReadonly: boolean;
  autoResume: boolean;
  initialLastContext?: AppUsage;
  initialMapArtifacts?: MapLayer[];
}) {
  const { visibilityType } = useChatVisibility({
    chatId: id,
    initialVisibilityType,
  });

  const { mutate } = useSWRConfig();
  const { layers, addLayers, clearLayers } = useMap();

  const [input, setInput] = useState<string>("");
  const [usage, setUsage] = useState<AppUsage | undefined>(initialLastContext);
  const [currentModelId, setCurrentModelId] = useState(initialChatModel);
  const currentModelIdRef = useRef(currentModelId);

  const currentAssistantIdRef = useRef<string | null>(null);
  const tempAssistantIdRef = useRef<string | null>(null);
  
  const [activeSuggestions, setActiveSuggestions] = useState<string[]>([]);

  useEffect(() => {
    currentModelIdRef.current = currentModelId;
  }, [currentModelId]);

  // Clear layers when switching to a new chat
  useEffect(() => {
    clearLayers();
    
    // Load initial artifacts for this chat
    if (initialMapArtifacts.length > 0) {
      addLayers(initialMapArtifacts);
    }
  }, [id]);

  // --- ADD THIS EFFECT TO FIX THE URL ISSUE ---
  useEffect(() => {
    if (id && typeof window !== "undefined") {
      const currentPath = window.location.pathname;
      // If we are at the root or generic chat path, update it to the specific ID
      if (currentPath === "/" || currentPath === "/chat") {
        window.history.replaceState({}, "", `/chat/${id}`);
      }
    }
  }, [id]);

  useEffect(() => {
    console.log("activeSuggestions state updated:", activeSuggestions);
  }, [activeSuggestions]);

  const {
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    regenerate,
    resumeStream,
  } = useChat<ChatMessage>({
    id,
    messages: initialMessages,
    experimental_throttle: 100,
    generateId: generateUUID,
    transport: new DefaultChatTransport({
      api: "/api/chat",
      fetch: fetchWithErrorHandlers,
      prepareSendMessagesRequest(request) {
        return {
          body: {
            id: request.id,
            message: request.messages.at(-1),
            selectedChatModel: currentModelIdRef.current,
            selectedVisibilityType: visibilityType,
            ...request.body,
          },
        };
      },
    }),
    onData: (dataPart) => {
      handleOnData({
        dataPart,
        setMessages,
        setUsage,
	setActiveSuggestions,
        currentAssistantIdRef,
        tempAssistantIdRef,
        addMapLayers: addLayers,
      });
    },
    onFinish: () => { 
      mutate(unstable_serialize(getChatHistoryPaginationKey)); 
    },
    onError: (error) => {
      // pass
    },
  });

  const searchParams = useSearchParams();
  const query = searchParams.get("query");
  const [hasAppendedQuery, setHasAppendedQuery] = useState(false);

  useEffect(() => {
    if (query && !hasAppendedQuery) {
      sendMessage({
        role: "user" as const,
        parts: [{ type: "text", text: query }],
      });

      setHasAppendedQuery(true);
      window.history.replaceState({}, "", `/chat/${id}`);
    }
  }, [query, sendMessage, hasAppendedQuery, id]);

  const { data: votes } = useSWR<Vote[]>(
    messages.length >= 2 ? `/api/vote?chatId=${id}` : null,
    fetcher
  );

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const isArtifactVisible = useArtifactSelector((state) => state.isVisible);

  useAutoResume({
    autoResume,
    initialMessages,
    resumeStream,
    setMessages,
  });
  
  const visibleMessages = messages.filter(isRenderableMessage);

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Full-width Chat Header */}
      <ChatHeader
        chatId={id}
        isReadonly={isReadonly}
        selectedVisibilityType={initialVisibilityType}
      />

      {/* Two-column layout below header */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left side: Chat */}
        <div className="flex h-full w-1/2 min-w-0 flex-col">
          <Messages
            chatId={id}
            isArtifactVisible={isArtifactVisible}
            isReadonly={isReadonly}
            messages={visibleMessages}
            regenerate={regenerate}
            selectedModelId={initialChatModel}
            setMessages={setMessages}
            status={status}
            votes={votes}
          />

          <div className="sticky bottom-0 z-1 mx-auto flex w-full max-w-4xl gap-2 border-t-0 bg-background px-2 pb-3 md:px-4 md:pb-4">
            {!isReadonly && (
              <MultimodalInput
                attachments={attachments}
                chatId={id}
                input={input}
                messages={messages}
                onModelChange={setCurrentModelId}
                selectedModelId={currentModelId}
                selectedVisibilityType={visibilityType}
                sendMessage={sendMessage}
                setAttachments={setAttachments}
                setInput={setInput}
                setMessages={setMessages}
		activeSuggestions={activeSuggestions} 
                status={status}
                stop={stop}
                usage={usage}
              />
            )}
          </div>
        </div>

        {/* Right side: Map (no header, just the map) */}
        <div className="relative h-full w-1/2 border-l border-zinc-200 dark:border-zinc-700">
          <FloodMap layers={layers} />
        </div>
      </div>

      {/* Artifact panel */}
      <Artifact
        attachments={attachments}
        chatId={id}
        input={input}
        isReadonly={isReadonly}
        messages={messages}
        regenerate={regenerate}
        selectedModelId={currentModelId}
        selectedVisibilityType={visibilityType}
        sendMessage={sendMessage}
        setAttachments={setAttachments}
        setInput={setInput}
        setMessages={setMessages}
        status={status}
        stop={stop}
        votes={votes}
      />
    </div>
  );

}
